// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Is this plan safe to execute?
 *
 * The old checks asked a question about the two *boards*: are they close enough
 * that a whole-region splice is defensible. That question was necessarily
 * pessimistic — a single net added anywhere refused a merge that touched no nets
 * at all — because the merge could not know which objects the user meant.
 *
 * Now it can. Every question here is scoped to the plan: not "do the boards have
 * the same nets" but "does Board A have the nets *these objects* reference".
 * That is both more permissive, because most merges do not touch the thing that
 * differs, and stricter, because it checks what will actually be written.
 *
 * Four levels, and the difference between them matters:
 *
 *   blocker  — would produce a file KiCad may refuse to load. No override.
 *   confirm  — legitimate, irreversible, and easy to do by accident.
 *   warning  — the merge is fine; something outside it now needs attention.
 *   note     — worth knowing, needs nothing.
 */

import type { Board } from '../model/board.ts';
import type { MaybeBBox } from '../model/bbox.ts';
import type { DiffResult } from '../diff/diff.ts';
import { groupMembers, MEMBER_CONTAINER_TYPES } from '../model/objects.ts';
import { computeEffects, type MergeEffects } from '../select/effects.ts';
import { GLOBAL_BLOCK_INFO, type BuiltPlan, type MergePlan } from '../select/plan.ts';

export type IssueLevel = 'blocker' | 'confirm' | 'warning' | 'note';

export interface PlanIssue {
  id: string;
  level: IssueLevel;
  /** What is wrong, or what is about to happen. */
  title: string;
  /** Why it matters, in the terms of the board rather than the file format. */
  detail: string;
  /** Changes the issue is about, so the UI can select them. */
  changeKeys?: string[];
  bbox?: MaybeBBox;
}

export interface PlanCheck {
  issues: PlanIssue[];
  blockers: PlanIssue[];
  /** Issues the user must explicitly confirm before the merge will run. */
  confirmations: PlanIssue[];
  effects: MergeEffects;
  /** True when nothing blocks. Confirmations may still be outstanding. */
  canMerge: boolean;
}

export function checkPlan(a: Board, b: Board, diff: DiffResult, built: BuiltPlan): PlanCheck {
  const { plan } = built;
  const effects = computeEffects(diff, plan);
  const issues: PlanIssue[] = [];

  // --- blockers -------------------------------------------------------------------

  if (effects.netsMissingInA.length > 0 && !plan.takeGlobal.has('nets')) {
    issues.push({
      id: 'net-missing',
      level: 'blocker',
      title: `${effects.netsMissingInA.length} net(s) do not exist in ${a.fileName}`,
      detail:
        `${effects.netsMissingInA.join(', ')} — objects you are taking are attached to these nets, and ` +
        `${a.fileName} has no such net to attach them to. Copper spliced onto a net that does not exist ` +
        `is not something this tool will write. Take the net table as well, or leave those objects behind.`,
    });
  }

  if (effects.layersMissingInA.length > 0) {
    issues.push({
      id: 'layer-missing',
      level: 'blocker',
      title: `${effects.layersMissingInA.join(', ')} do(es) not exist in ${a.fileName}`,
      detail:
        `Objects you are taking draw on ${effects.layersMissingInA.join(', ')}, which ${a.fileName} does ` +
        `not have. Take Board B's layer stack as well, or leave those objects behind.`,
    });
  }

  for (const dangling of danglingMembers(diff, plan, b)) {
    issues.push({
      id: 'dangling-member',
      level: 'blocker',
      title: `${dangling.container} refers to an object that will not be in the file`,
      detail:
        `Its \`members\` list names ${dangling.missing.length} UUID(s) that neither board can supply. ` +
        `KiCad may refuse to load a file with a dangling group reference, so this merge is refused.`,
    });
  }

  // --- confirmations ---------------------------------------------------------------

  for (const block of plan.takeGlobal) {
    issues.push({
      id: `global-${block}`,
      level: 'confirm',
      title: `Replace ${a.fileName}'s ${GLOBAL_BLOCK_INFO[block].label.toLowerCase()} with Board B's`,
      detail: `${GLOBAL_BLOCK_INFO[block].help} Board A's version is discarded, not merged.`,
    });
  }

  const deletions = Object.values(effects.removed).reduce((n, x) => n + x, 0);
  if (deletions > 0) {
    issues.push({
      id: 'deletions',
      level: 'confirm',
      title: `Delete ${deletions} object(s) from ${a.fileName}`,
      detail:
        'These exist in Board A and not in Board B, so taking those changes removes them. Nothing is ' +
        'written until you download the result, but the deletion is not recoverable from the output file.',
    });
  }

  // --- warnings ---------------------------------------------------------------------

  if (effects.netsIntroduced.length > 0) {
    issues.push({
      id: 'nets-introduced',
      level: 'warning',
      title: `${effects.netsIntroduced.length} new net(s) will be created`,
      detail:
        `${effects.netsIntroduced.slice(0, 6).join(', ')}${effects.netsIntroduced.length > 6 ? '…' : ''} — this ` +
        `board names nets on the objects themselves rather than in a table, so an object arriving with a name ` +
        `${a.fileName} has not seen brings that net into existence. Check they are the nets you meant.`,
    });
  }

  if (effects.unselected > 0) {
    issues.push({
      id: 'unselected',
      level: 'warning',
      title: `${effects.unselected} difference(s) are in no selection`,
      detail:
        'They will not be merged, and Board A keeps its own version of each. This is the easiest ' +
        'mistake to make: check the list before you commit to it.',
    });
  }

  if (effects.zonesNeedingRefill > 0) {
    issues.push({
      id: 'zone-refill',
      level: 'warning',
      title: `${effects.zonesNeedingRefill} zone(s) must be re-filled`,
      detail:
        'Zone fill caches are discarded, because a fill computed against different surroundings is wrong ' +
        'and looks right. Run Edit → Fill All Zones in KiCad before sending this anywhere.',
    });
  }

  for (const note of built.notes) {
    issues.push({
      id: `closure-${note.kind}`,
      level: note.kind === 'pulled-in' ? 'note' : 'warning',
      title: note.kind === 'pulled-in' ? 'Some changes were added to keep groups intact' : 'Some objects will lose their grouping',
      detail: note.message,
      changeKeys: note.changeKeys,
    });
  }

  const blockers = issues.filter((i) => i.level === 'blocker');
  const confirmations = issues.filter((i) => i.level === 'confirm');

  return {
    issues,
    blockers,
    confirmations,
    effects,
    canMerge: blockers.length === 0 && effects.total + plan.takeGlobal.size > 0,
  };
}

interface Dangling {
  container: string;
  missing: string[];
}

/**
 * Containers whose members cannot be satisfied by anything.
 *
 * Closure pulls in members that *have* a change; this catches the residue — a
 * `members` entry naming a UUID that exists in neither board, which is a file
 * that was already broken, and one this tool must not propagate.
 */
function danglingMembers(diff: DiffResult, plan: MergePlan, b: Board): Dangling[] {
  const out: Dangling[] = [];

  // Indexed up front. A real revision carries tens of thousands of changes, and
  // a scan per member reference would be quadratic on exactly the boards where
  // this check matters most.
  const knownInA = new Set<string>();
  for (const o of diff.a.objects) if (o.uuid !== undefined) knownInA.add(o.uuid);
  const knownInB = new Set<string>();
  for (const o of b.objects) if (o.uuid !== undefined) knownInB.add(o.uuid);
  const changeByUuid = new Map<string, string>();
  for (const c of diff.substantive) {
    for (const side of [c.b, c.a]) {
      if (side?.uuid !== undefined && side.uuid !== '') changeByUuid.set(side.uuid, c.key);
    }
  }

  for (const change of diff.substantive) {
    if (!plan.take.has(change.key) || change.b === undefined) continue;
    if (!MEMBER_CONTAINER_TYPES.has(change.b.type)) continue;

    const missing = groupMembers(change.b.node).filter((uuid) => {
      if (knownInA.has(uuid)) return false;
      if (!knownInB.has(uuid)) return true;
      // Present in Board B: it reaches the output only if its change is taken.
      const key = changeByUuid.get(uuid);
      return key !== undefined && !plan.take.has(key);
    });

    if (missing.length > 0) out.push({ container: `A ${change.b.type}`, missing });
  }
  return out;
}
